import { useWatch } from 'react-hook-form-v7';
import type { Control } from 'react-hook-form-v7';
import * as yup from 'yup';

import { VerificationStatus } from '@personnel/domain/types';
import type { VerificationStatusType } from '@personnel/domain/types';
import { toVerificationStatus } from '@personnel/presentation/components/form/EmployeeForm/hooks/usePhoneVerificationBadge';
import type { EmployeeFormData } from '@personnel/presentation/types';

const emailShape = yup.string().email();

const normalize = (value: string): string => value.trim().toLowerCase();

/**
 * The verification state to show beside the email field while it is being typed.
 *
 * Three rules, in this order. A value that is not an email yet shows nothing, because half a
 * sentence is neither verified nor pending. Typing the original address back restores the state
 * that arrived, so undoing a change undoes the badge with it. Any other valid address is
 * unverified: verifying means opening the link, which happens on another screen.
 *
 * @param params - The live email, the one that arrived and the state it arrived with
 * @returns The state to paint, or undefined when there is nothing to say
 */
export const resolveEmailVerificationStatus = ({
  email,
  initialEmail,
  initialStatus,
}: {
  email?: string;
  initialEmail?: string;
  initialStatus?: VerificationStatusType;
}): VerificationStatusType | undefined => {
  // Yup calls the empty string a valid optional email, so emptiness is ruled out first.
  const value = email?.trim() ?? '';
  if (!value || !emailShape.isValidSync(value)) return undefined;

  if (initialEmail && normalize(value) === normalize(initialEmail)) return initialStatus;

  return VerificationStatus.NOT_VERIFIED;
};

/**
 * The badge state for the email field, read from the form itself.
 *
 * Creation has no badge: there is no account yet whose state could be shown.
 *
 * The state comes from the requirement of this one value. The account-wide status is only the
 * fallback for a response that did not publish the requirements, where saying nothing would drop a
 * badge that used to be there.
 *
 * @param control - The employee form control
 * @returns The state to paint, or undefined when there is nothing to say
 */
export const useEmailVerificationBadge = (control: Control<EmployeeFormData>): VerificationStatusType | undefined => {
  const isEditMode = useWatch({ control, name: '_isEditMode' });
  const email = useWatch({ control, name: 'email' });
  const initialEmail = useWatch({ control, name: '_initialEmail' });
  const accountStatus = useWatch({ control, name: '_verificationStatus' });
  const requirements = useWatch({ control, name: '_verificationRequirements' });

  if (!isEditMode) return undefined;

  const initialStatus = toVerificationStatus(requirements?.EMAIL) ?? accountStatus;

  return resolveEmailVerificationStatus({ email, initialEmail, initialStatus });
};
